/**
 * <s-load-more> — appends the next page of a paginated product grid in place.
 *
 * Progressive enhancement: the trigger is a real link to the next page, so it works
 * without JavaScript. With JavaScript, clicking fetches that page, appends its grid items
 * to the target list, updates the "showing" count, and swaps in the next page link (or
 * removes the control on the last page).
 *
 * Expected DOM (rendered by templates/collection.liquid):
 *   <s-load-more data-target="product-grid">
 *     <p data-load-more-status>…</p>
 *     <a data-load-more-link href="?page=2">Load more</a>
 *   </s-load-more>
 */
class SLoadMore extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector("[data-load-more-link]");
    if (!this.link) return;
    this.onClick = (event) => this.load(event);
    this.link.addEventListener("click", this.onClick);
  }

  disconnectedCallback() {
    this.link?.removeEventListener("click", this.onClick);
  }

  /** @param {MouseEvent} event */
  async load(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
    event.preventDefault();
    if (this.busy) return;

    const target = document.getElementById(this.dataset.target || "");
    if (!target) {
      window.location.href = this.link.href;
      return;
    }

    this.busy = true;
    const label = this.link.textContent;
    this.link.setAttribute("aria-busy", "true");
    if (this.dataset.loadingLabel) this.link.textContent = this.dataset.loadingLabel;

    try {
      const response = await fetch(this.link.href, { headers: { Accept: "text/html" } });
      if (!response.ok) throw new Error(String(response.status));
      const html = new DOMParser().parseFromString(await response.text(), "text/html");

      const nextGrid = html.getElementById(target.id);
      const firstNew = nextGrid?.firstElementChild;
      if (nextGrid) target.append(...Array.from(nextGrid.children));

      const nextControl = html.querySelector("s-load-more");
      const status = this.querySelector("[data-load-more-status]");
      const nextStatus = nextControl?.querySelector("[data-load-more-status]");
      if (status && nextStatus) status.textContent = nextStatus.textContent;

      const nextLink = nextControl?.querySelector("[data-load-more-link]");
      if (nextLink) {
        this.link.href = nextLink.getAttribute("href");
        this.link.textContent = label;
        this.link.removeAttribute("aria-busy");
      } else {
        this.link.remove();
      }

      // Move focus to the first new product so keyboard users continue from there.
      const focusable = firstNew?.querySelector("a[href]");
      if (focusable instanceof HTMLElement) focusable.focus({ preventScroll: true });
    } catch {
      window.location.href = this.link.href;
    } finally {
      this.busy = false;
    }
  }
}

if (!customElements.get("s-load-more")) {
  customElements.define("s-load-more", SLoadMore);
}
